import { Badge, Heading, Table, Tbody, Td, Tr } from '@chakra-ui/react';
import { add, format, fromUnixTime } from 'date-fns';
import { useCallback, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'next-i18next';

import { EventType } from '@lib/types';
import { ThemeMode, useThemeMode } from '@providers/ThemeModeProvider';

import { FirebaseContext } from '../../contexts';

import BlockTitle from './blocks/BlockTitle';

type Props = {
  event: Pick<EventType, 'agendas' | 'startedAt' | 'id'>;
  onClick?: (index: number) => void;
  mode?: ThemeMode;
};

function getAgendaItemTime({
  startedAt,
  durationInMins,
  accumulateDurationInMins,
}: {
  startedAt: number;
  durationInMins: number;
  accumulateDurationInMins: number;
}) {
  const eventStartDateTime = fromUnixTime(startedAt);

  const startDateTime = add(eventStartDateTime, {
    minutes: accumulateDurationInMins,
  });
  const endDateTime = add(startDateTime, { minutes: durationInMins });

  const formattedStartTime = format(startDateTime, 'HH:mm');
  const formattedEndTime = format(endDateTime, 'HH:mm');

  return `${formattedStartTime} - ${formattedEndTime}`;
}

export const AgendaTable = ({
  event,
  currentAgendaIndex,
  onClick,
  mode,
}: Props & { currentAgendaIndex?: number }) => {
  const { t } = useTranslation('chime');

  const isDarkMode = mode === ThemeMode.Dark;

  return (
    <Table variant="striped" colorScheme={isDarkMode ? 'blackAlpha' : 'gray'}>
      <Tbody>
        {event.agendas.map((agenda, idx) => (
          <Tr
            key={agenda.title}
            cursor={onClick ? 'pointer' : 'default'}
            onClick={onClick ? () => onClick(idx) : undefined}
            _hover={onClick ? { filter: 'brightness(90%)' } : {}}
          >
            <Td fontWeight="bold">{agenda.title}</Td>
            <Td>
              {getAgendaItemTime({
                startedAt: event.startedAt,
                durationInMins: event.agendas[idx].durationInMins,
                accumulateDurationInMins: event.agendas
                  .slice(0, idx)
                  .reduce(
                    (accumulateMins, agendaItem) =>
                      accumulateMins + Number(agendaItem.durationInMins),
                    0
                  ),
              })}
            </Td>
            <Td>
              {currentAgendaIndex === idx && (
                <Badge colorScheme="red">{t('chime:Ongoing')}</Badge>
              )}
            </Td>
          </Tr>
        ))}
      </Tbody>
    </Table>
  );
};

function Agenda({ event, onClick }: Props) {
  const { firebase } = useContext(FirebaseContext);
  const { t } = useTranslation('chime');
  const { styles, mode } = useThemeMode();

  const [currentAgendaIndex, setCurrentAgendaIndex] = useState(-1);

  const handleListenCurrentAgendaChange = useCallback(() => {
    if (!firebase) {
      return;
    }

    firebase
      .database()
      .ref(`/${process.env.NODE_ENV}/events/${event.id}/currentAgendaIndex`)
      .on('value', (snapshot) => {
        setCurrentAgendaIndex(snapshot.val());
      });
  }, [event.id, firebase]);

  useEffect(() => {
    handleListenCurrentAgendaChange();
  }, [handleListenCurrentAgendaChange]);

  if (!event.agendas) {
    return null;
  }

  return (
    <>
      <BlockTitle title={t('Agendas')} />
      {onClick && (
        <Heading as="h6" fontSize="sm" m={4} color={styles.label}>
          {t('chime:Click agenda to mark it as ongoing')}
        </Heading>
      )}
      <AgendaTable
        mode={mode}
        event={event}
        onClick={onClick}
        currentAgendaIndex={currentAgendaIndex}
      />
    </>
  );
}

export default Agenda;
